/**
 * 微信 JS-SDK 分享统一封装
 *
 * - 官方 JS-SDK 1.6.0 动态加载（https://res.wx.qq.com），无新增 npm 依赖
 * - 签名由后端 /share/jssdk/signature 生成，前端 wx.config 后调用：
 *     updateAppMessageShareData（微信好友 / 群聊）
 *     updateTimelineShareData（朋友圈）
 * - 内容优先级：单页面自定义 > 后台默认分享内容
 * - 俱乐部身份（MERCHANT）分享链接自动拼接 invite_code（其 OID），普通用户链接保持原生
 * - 非微信环境 / 未配置 / 签名失败：全部静默降级，不影响页面正常浏览
 */
import { getShareConfig, getJsSdkSignature, type ShareConfigVO } from '@/api/share'
import { useUserStore } from '@/store'
import { buildInviteLink } from '@/utils/invite'

/** 官方 JS-SDK 地址（固定官方域名，禁止第三方替换） */
const WX_JSSDK_URL = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'

/** 官方分享接口白名单：好友/群聊 + 朋友圈（与后端 ShareConstants.JS_API_LIST 对应） */
const WX_JS_API_LIST = ['updateAppMessageShareData', 'updateTimelineShareData']

/** 单页面自定义分享内容（缺省字段自动回退后台默认值） */
export interface SharePageData {
  title?: string
  desc?: string
  imgUrl?: string
  /** 自定义分享落地链接（如邀友活动带 invite_token 的落地页、结束态的首页）；缺省用当前页地址 */
  link?: string
}

/** 内置默认分享封面（随 H5 包部署，公网可访问，缺图时统一兜底，禁止出现无图卡片） */
const DEFAULT_SHARE_COVER = '/share/default.png'

/** 会员资料类分享（资料卡/上墙资料/会员详情）默认头像：无审核通过头像时兜底 */
const DEFAULT_SHARE_AVATAR = '/share/default-avatar.png'

/** 分享卡片统一品牌名（标题后缀与最终兜底，禁止各处硬编码） */
const SHARE_BRAND_NAME = 'AI牵线资料库'

/** 会员资料分享主标题前缀（主标题格式：会员资料 昵称） */
const PROFILE_SHARE_TITLE_PREFIX = '会员资料'

/** 会员资料分享副标题（统一文案，禁止各页面各写一套） */
const PROFILE_SHARE_DESC = '优质青年聚集地，高效遇见对的人'

/** 昵称最大展示长度，超出以省略号收尾（微信卡片标题单行容量有限） */
const PROFILE_SHARE_NAME_MAX = 12

/** 轨迹分享主标题模板（{name}=发布者昵称）：昵称发布了新的轨迹 */
const TRAIL_SHARE_TITLE_FMT = '{name}发布了新的轨迹'
/** 轨迹分享主标题昵称兜底（昵称为空/被脱敏为「-」时） */
const TRAIL_SHARE_NAME_FALLBACK = 'TA'
/** 轨迹分享昵称最大展示长度，超出以省略号收尾（微信卡片标题单行容量有限） */
const TRAIL_SHARE_NAME_MAX = 16
/** 轨迹分享副标题（正文）最大长度，超出截断（微信卡片副标题约两行容量） */
const TRAIL_SHARE_DESC_MAX = 60
/** 轨迹分享副标题兜底（正文为空时使用，避免回退到后台邀友默认文案） */
const TRAIL_SHARE_DESC_FALLBACK = '记录生活轨迹，发现与你同频的灵魂'
/** 轨迹分享默认封面（无首图时兜底，避免卡片展示邀友海报） */
const TRAIL_SHARE_COVER = '/share/trail.png'

/**
 * 路由级分享内容注册中心：按路由 name 配置标题/描述/封面。
 * 详情类页面在数据加载后可通过 setShareData 覆盖为业务内容（用户头像、活动封面等）。
 * 封面图片位于 public/share/，经 resolveAbsolute 补全为公网绝对地址。
 */
const ROUTE_SHARE: Record<string, SharePageData> = {
  // AI 红娘（入口/问答/房间/牵线记录全系列）
  AiMatch: { title: 'AI红娘｜智能牵线，懂你心意', desc: 'AI 红娘深度了解你的心意，定向匹配合适的 TA', imgUrl: '/share/ai.png' },
  AiMatchOld: { title: 'AI红娘｜智能牵线，懂你心意', desc: 'AI 红娘深度了解你的心意，定向匹配合适的 TA', imgUrl: '/share/ai.png' },
  AiMatchQa: { title: 'AI红娘正在了解你的心意', desc: '回答几个问题，让 TA 更快与你相遇', imgUrl: '/share/ai.png' },
  AiMatchRoom: { title: 'AI红娘｜线上面基房间', desc: '安全轻松的线上约会，从这里开始', imgUrl: '/share/ai.png' },
  AiMatchHistory: { title: '我的 AI 牵线记录', desc: '查看每一次缘分的牵线与反馈', imgUrl: '/share/ai.png' },
  AiMatchAppointments: { title: '牵线聊天记录', desc: 'AI 红娘为你安排的每一次相遇', imgUrl: '/share/ai.png' },
  AiMatchRecordDetail: { title: '牵线详情', desc: '查看本次牵线的完整反馈', imgUrl: '/share/ai.png' },
  // AI 情感助理介绍页（分享链接游客直达介绍页，点「立即开始」才引导登录）
  AiAssistantIntro: {
    title: `AI 情感助理 · ${SHARE_BRAND_NAME}`,
    desc: '专属AI红娘：性格深析、牵手前背调、隐私全程守护',
    imgUrl: '/share/ai.png'
  },
  // 性格测评（介绍页/答题/结果页共用同一路由；分享链接游客直达介绍页）
  PersonalityAssessment: {
    title: `性格偏好测评 · ${SHARE_BRAND_NAME}`,
    desc: '免费专业性格测评，结果纳入 AI 红娘牵线参考',
    imgUrl: DEFAULT_SHARE_COVER
  },
  MbtiStandardTest: {
    title: `MBTI 标准版测试 · ${SHARE_BRAND_NAME}`,
    desc: '93 题标准版 MBTI 量表，读懂人格底色，遇见同频的人',
    imgUrl: DEFAULT_SHARE_COVER
  },
  // 每周CP
  BlindActivity: { title: '每周CP｜每周滚动发车，0成本匿名破冰', desc: '系统自动开奖配对，VIP 还可定向锁定心仪对象', imgUrl: '/share/blind.png' },
  BlindFilter: { title: '每周CP｜设置你的心动筛选', desc: '告诉系统你的偏好，匹配更合心意', imgUrl: '/share/blind.png' },
  BlindResult: { title: '每周CP｜我的匹配结果', desc: '看看这次每周CP为你匹配到了谁', imgUrl: '/share/blind.png' },
  BlindHistory: { title: '每周CP｜我的参与记录', desc: '回顾每一期CP缘分', imgUrl: '/share/blind.png' },
  // 活动
  Activity: { title: '精彩活动｜线上线下派对，扩大交友圈', desc: '周末派对、主题联谊，即刻出发遇见同频的人', imgUrl: '/share/activity.png' },
  // 邀友送花瓣（活动页加载个人信息后覆盖标题/封面/落地链接；结束态覆盖为首页链接）
  InvitePetal: { title: '邀好友得花瓣｜邀请好友注册领花瓣', desc: '好友完成注册即可获得花瓣奖励，快来邀请身边的单身朋友', imgUrl: DEFAULT_SHARE_COVER },
  // 邀友落地页（线上资料库介绍，游客可访问；当前页地址自带 invite_token，无需额外拼参）
  InviteLanding: { title: '好友邀您脱单｜AI线上资料库', desc: 'AI 红娘牵线，高效真诚无套路，快来看看吧', imgUrl: DEFAULT_SHARE_COVER },
  // 轨迹
  Trail: { title: '足迹轨迹｜步履不停，山海终会相见', desc: '记录生活轨迹，发现与你同频的灵魂', imgUrl: '/share/trail.png' },
  // 轨迹详情（数据加载后由 buildTrailShareData 覆盖为「昵称发布了新的轨迹」+ 正文摘要；
  // 数据未到/游客打开接口失败时用此基线，避免回退到后台邀友默认卡片）
  TrailDetail: {
    title: `${TRAIL_SHARE_NAME_FALLBACK}发布了新的轨迹`,
    desc: TRAIL_SHARE_DESC_FALLBACK,
    imgUrl: TRAIL_SHARE_COVER
  },
  // 上墙资料库（上墙资料列表）
  Corner: { title: '上墙资料库｜优质会员，云端相见', desc: '实名认证优质会员，资料完整放心了解', imgUrl: '/share/corner.png' },
  // 会员资料详情（上墙资料/会员详情/资料卡）：数据加载后由 buildProfileShareData 覆盖昵称与头像
  CornerDetail: { title: '会员资料', desc: PROFILE_SHARE_DESC, imgUrl: DEFAULT_SHARE_AVATAR },
  MatchDetail: { title: '会员资料', desc: PROFILE_SHARE_DESC, imgUrl: DEFAULT_SHARE_AVATAR },
  ProfileCard: { title: '会员资料', desc: PROFILE_SHARE_DESC, imgUrl: DEFAULT_SHARE_AVATAR },
  // 会员中心
  VipCenter: { title: '会员中心｜开通会员，解锁更多交友权益', desc: '无限畅聊、优先匹配、访客可见，更多权益等你', imgUrl: '/share/vip.png' }
}

/** 当前路由的基线分享内容（页面 setShareData(null) 时回退到它，而不是空白） */
let routeBase: SharePageData | null = null

declare global {
  interface Window {
    wx?: any
  }
}

let sdkPromise: Promise<void> | null = null
let configPromise: Promise<ShareConfigVO | null> | null = null
/** 已完成 wx.config 的签名地址（hash 路由 # 前地址不变，避免重复签名） */
let signedUrl = ''
let defaultConfig: ShareConfigVO | null = null
let pageData: SharePageData | null = null

/** 是否微信内置浏览器 */
export function isWechat(): boolean {
  return /micromessenger/i.test(navigator.userAgent)
}

function loadWxSdk(): Promise<void> {
  if (window.wx) return Promise.resolve()
  if (sdkPromise) return sdkPromise
  sdkPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = WX_JSSDK_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      sdkPromise = null
      reject(new Error('jweixin sdk load failed'))
    }
    document.head.appendChild(script)
  })
  return sdkPromise
}

function loadShareConfig(): Promise<ShareConfigVO | null> {
  if (defaultConfig) return Promise.resolve(defaultConfig)
  if (configPromise) return configPromise
  configPromise = getShareConfig()
    .then((res) => {
      if (res?.code === 200 && res.data) {
        defaultConfig = res.data
        return res.data
      }
      return null
    })
    .catch(() => null)
  return configPromise
}

/** 图片地址补全为绝对地址（微信要求 imgUrl 必须公网可访问完整 URL） */
function resolveAbsolute(url?: string): string {
  const v = (url || '').trim()
  if (!v) return ''
  if (/^https?:\/\//i.test(v)) return v
  try {
    return new URL(v, window.location.origin).toString()
  } catch {
    return ''
  }
}

/** 合并页面自定义与后台默认内容，并生成携带邀请码的分享链接 */
function resolveSharePayload() {
  const config = defaultConfig || {}
  const merchant = useUserStore()
  // 俱乐部身份（商户/主理人）携带本俱乐部邀请码；普通用户原生链接
  const inviteCode = merchant.isMerchant && merchant.oid ? merchant.oid : ''
  // 页面/路由显式指定的分享链接优先（邀友活动落地页、活动结束分享首页等）
  const customLink = (pageData?.link || routeBase?.link || '').trim()
  const link = customLink || buildInviteLink(window.location.href, inviteCode)

  // 优先级：页面动态内容 > 路由基线 > 后台默认配置 > 页面标题 > 分享品牌兜底，任何一级都不允许标题/封面为空
  const title = (pageData?.title || routeBase?.title || config.title
    || document.title || SHARE_BRAND_NAME).trim()
  const desc = (pageData?.desc || routeBase?.desc || config.desc || '优质单身实名资料，高效遇见对的人').trim()
  const imgUrl = resolveAbsolute(pageData?.imgUrl || routeBase?.imgUrl || config.cover || DEFAULT_SHARE_COVER)

  return { title, desc, link, imgUrl }
}

/**
 * 同步 OpenGraph / Twitter Card meta 标签。
 * 微信内 JS-SDK 未配置或外部浏览器分享时，卡片信息由此提供（hash SPA 也能在运行时按页更新）。
 */
function syncMetaTags(payload: { title: string; desc: string; imgUrl: string }) {
  const set = (selector: string, attr: string, content: string) => {
    let el = document.head.querySelector<HTMLMetaElement>(selector)
    if (!el) {
      el = document.createElement('meta')
      const m = /property="([^"]+)"/.exec(selector)
      if (m) el.setAttribute('property', m[1])
      const n = /name="([^"]+)"/.exec(selector)
      if (n) el.setAttribute('name', n[1])
      document.head.appendChild(el)
    }
    el.setAttribute(attr, content)
  }
  set('meta[property="og:title"]', 'content', payload.title)
  set('meta[property="og:description"]', 'content', payload.desc)
  set('meta[property="og:image"]', 'content', payload.imgUrl)
  set('meta[property="og:url"]', 'content', window.location.href)
  set('meta[name="twitter:title"]', 'content', payload.title)
  set('meta[name="twitter:description"]', 'content', payload.desc)
  set('meta[name="twitter:image"]', 'content', payload.imgUrl)
}

/** 应用当前分享内容：同步 meta 标签 + 注册到微信 JS-SDK */
function applyAllShare() {
  const payload = resolveSharePayload()
  syncMetaTags(payload)
  // 公众号未配置 JS-SDK 时，微信卡片标题直接抓取 document.title（SPA 的 og:title 不稳定生效），
  // 必须把分享标题同步到 document.title，否则卡片仍显示「xxx · 站点名」的路由标题
  if (typeof document !== 'undefined' && document.title !== payload.title) {
    document.title = payload.title
  }
  const wx = window.wx
  if (wx && signedUrl) {
    applyShareData(wx)
  }
}

/** 在 wx.ready 后注册双场景分享内容 */
function applyShareData(wx: any) {
  const payload = resolveSharePayload()
  // 微信好友 / 群聊
  wx.updateAppMessageShareData({
    title: payload.title,
    desc: payload.desc,
    link: payload.link,
    imgUrl: payload.imgUrl,
    success: () => {},
    fail: () => console.warn('[wechatShare] updateAppMessageShareData 失败')
  })
  // 朋友圈（朋友圈无 desc 字段）
  wx.updateTimelineShareData({
    title: payload.title,
    link: payload.link,
    imgUrl: payload.imgUrl,
    success: () => {},
    fail: () => console.warn('[wechatShare] updateTimelineShareData 失败')
  })
}

/**
 * 路由切换后调用：完成 SDK 加载 + wx.config 鉴权（仅微信环境）。
 * hash 路由下签名 URL 取 # 之前的完整地址，iOS / Android 通用。
 */
export async function setupWechatShare(): Promise<void> {
  if (!isWechat()) return
  try {
    const config = await loadShareConfig()
    const appId = (config?.appId || '').trim()
    if (!appId) {
      // 后台未配置公众号：静默降级为微信默认抓取分享
      console.warn('[wechatShare] 未配置微信公众号 AppID，跳过 JS-SDK 初始化')
      return
    }
    const signUrl = window.location.href.split('#')[0]
    await loadWxSdk()
    const wx = window.wx
    if (!wx) return

    if (signedUrl === signUrl) {
      applyShareData(wx)
      return
    }

    const res = await getJsSdkSignature(signUrl)
    const sign = res?.data
    if (res?.code !== 200 || !sign?.signature) {
      console.warn('[wechatShare] 获取JS-SDK签名失败，降级默认分享')
      return
    }
    wx.config({
      debug: false,
      appId: sign.appId || appId,
      timestamp: sign.timestamp,
      nonceStr: sign.nonceStr,
      signature: sign.signature,
      jsApiList: WX_JS_API_LIST
    })
    wx.error((err: unknown) => {
      // 配置失败仅记录（常见为域名不一致），不打扰用户
      console.warn('[wechatShare] wx.config 鉴权失败', err)
    })
    wx.ready(() => {
      signedUrl = signUrl
      applyShareData(wx)
    })
  } catch (e) {
    console.warn('[wechatShare] 初始化异常，降级默认分享', e)
  }
}

/**
 * 路由切换时调用：按路由注册中心设置该页基线分享内容（标题/描述/封面），
 * 并同步 OG meta。未在注册中心登记的路由使用站点默认封面。
 */
export function setRouteShare(routeName?: string | symbol | null): void {
  routeBase = (routeName && ROUTE_SHARE[String(routeName)]) || null
  // 切路由先清掉上一个页面的动态覆盖，避免 A 页内容串到 B 页
  pageData = null
  applyAllShare()
  if (isWechat()) {
    void setupWechatShare()
  }
}

/**
 * 构造会员资料类分享内容（资料卡 / 上墙资料 / 会员详情统一格式）。
 * - 主标题：会员资料 昵称（昵称超长自动省略，缺昵称用「会员」兜底）
 * - 副标题：统一文案 PROFILE_SHARE_DESC
 * - 封面：用户审核通过头像；头像为空 / 占位「-」/ 未审核通过（后端标记 blur=1）时用默认头像
 * @param userName 昵称
 * @param avatar 头像地址
 */
export function buildProfileShareData(
  userName?: string | null,
  avatar?: string | null
): SharePageData {
  const rawName = (userName || '').trim()
  const name = rawName && rawName !== '-' ? rawName : '会员'
  const displayName = name.length > PROFILE_SHARE_NAME_MAX
    ? `${name.slice(0, PROFILE_SHARE_NAME_MAX)}…`
    : name

  const rawAvatar = (avatar || '').trim()
  const approvedAvatar = rawAvatar && rawAvatar !== '-' && !/[?&]blur=1/i.test(rawAvatar)
    ? rawAvatar
    : ''

  return {
    title: `${PROFILE_SHARE_TITLE_PREFIX} ${displayName}`,
    desc: PROFILE_SHARE_DESC,
    imgUrl: approvedAvatar || DEFAULT_SHARE_AVATAR
  }
}

/**
 * 富文本正文归一化为分享纯文本：
 * 换行类标签转换行 → 去剩余标签 → 解码常见 HTML 实体 → 合并空白（含换行）为单空格。
 * 历史纯文本内容原样走空白合并，不影响展示。
 */
function normalizeTrailContent(raw?: string | null): string {
  let s = String(raw || '')
  if (/<[^>]+>/.test(s)) {
    s = s
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6])\s*>/gi, '\n')
      .replace(/<[^>]+>/g, '')
    // 借助 textarea 解码 &amp; &nbsp; &#xxx; 等实体（分享仅在浏览器端调用）
    try {
      const decoder = document.createElement('textarea')
      decoder.innerHTML = s
      s = decoder.value
    } catch {
      s = s.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    }
  }
  return s.replace(/\s+/g, ' ').trim()
}

/**
 * 构造轨迹详情分享内容（轨迹详情页统一入口）。
 * - 主标题：昵称发布了新的轨迹（昵称超长省略，缺省用 TA）
 * - 副标题：轨迹正文摘要（富文本转纯文本，超长截断；正文为空用统一兜底文案）
 * - 封面：轨迹首图；无图用轨迹默认封面，避免回退为邀友海报
 * @param userName 发布者昵称
 * @param content 轨迹正文（可能为富文本 HTML）
 * @param imgUrl 轨迹首图（可选）
 */
export function buildTrailShareData(
  userName?: string | null,
  content?: string | null,
  imgUrl?: string | null
): SharePageData {
  const rawName = (userName || '').trim()
  const name = rawName && rawName !== '-' ? rawName : TRAIL_SHARE_NAME_FALLBACK
  const displayName = name.length > TRAIL_SHARE_NAME_MAX
    ? `${name.slice(0, TRAIL_SHARE_NAME_MAX)}…`
    : name
  const text = normalizeTrailContent(content)

  return {
    title: TRAIL_SHARE_TITLE_FMT.replace('{name}', displayName),
    desc: text.slice(0, TRAIL_SHARE_DESC_MAX) || TRAIL_SHARE_DESC_FALLBACK,
    imgUrl: (imgUrl || '').trim() || TRAIL_SHARE_COVER
  }
}

/**
 * 页面级自定义分享内容（优先级高于路由基线/后台默认）。
 * 传入 null 恢复为当前路由基线内容（而非空白卡片）。
 */
export function setShareData(data: SharePageData | null): void {
  pageData = data
  // 任何环境都同步 OG meta（微信未配置 JS-SDK 及外部浏览器分享依赖它）
  try {
    applyAllShare()
  } catch (e) {
    console.warn('[wechatShare] 更新分享内容失败', e)
  }
  const wx = window.wx
  // SDK 已就绪时立即生效；未就绪时 setupWechatShare 的 wx.ready 会读取最新值
  if (wx && signedUrl) {
    try {
      applyShareData(wx)
    } catch (e) {
      console.warn('[wechatShare] 更新微信分享内容失败', e)
    }
  } else if (isWechat()) {
    void setupWechatShare()
  }
}
